import { useState, useEffect } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

// Verdict is the operator's standing on this SKU vs THIS rival (your price vs theirs).
// 'no_own_price' = the operator doesn't carry / hasn't collected the SKU.
export type PriceVerdict = 'higher' | 'cheaper' | 'parity' | 'no_own_price'

export interface PriceRow {
  competitor_id: string
  competitor_name: string
  raw_name: string       // product name as scraped (before normalization)
  brand: string | null   // menu_items.brand — populated from source (~77%); NULL/'' = unbranded
  category: string | null
  price: number
  on_sale: boolean       // on_promo in API
  discount_label?: string
  last_updated: string
  your_price: number | null   // operator's OWN price for this SKU at this location (is_own menu_items)
  gap: number | null          // your_price − rival price (positive = you're higher); null when not carried
  verdict: PriceVerdict
  // Rival's operator type. Selected by the API and spread onto every row at
  // runtime; declared here so the By-rival view can read it off PriceRow.
  business_type?: 'storefront' | 'delivery' | null
}

export function usePriceMatrix(locationId: string | null, category?: string, type?: string) {
  const authFetch = useAuthFetch()
  const [matrix, setMatrix] = useState<PriceRow[]>([])
  // Category vocabulary from the API: flower size buckets + non-flower categories
  // with data for this location's rivals. Stable across the active filter.
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!locationId) return
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({ location_id: locationId })
    if (category) params.set('category', category)
    if (type && type !== 'both') params.set('type', type)

    authFetch(`${API}/api/v1/prices/matrix?${params}`)
      .then((r) => r.json())
      .then((data) => {
        const rows = (data.data?.matrix || []).map((r: any) => ({
          ...r,
          price: parseFloat(r.price),
          your_price: r.your_price != null ? Number(r.your_price) : null,
          gap: r.gap != null ? Number(r.gap) : null,
          verdict: r.verdict ?? 'no_own_price',
        }))
        setMatrix(rows)
        if (Array.isArray(data.data?.categories)) setCategories(data.data.categories)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [locationId, category, type])

  return { matrix, categories, loading, error }
}
